import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  DollarSign, Target, Shield, Activity, RefreshCw, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RiskSection({ embedded = false }) {
  const [dashboard, setDashboard] = useState(null);
  const [riskReport, setRiskReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bankrollInput, setBankrollInput] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [dashboardRes, reportRes] = await Promise.all([
        fetch(`${API_URL}/api/risk/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/risk/report`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setDashboard(data);
        setBankrollInput(data.bankroll?.current?.toString() || '1000');
      }
      
      if (reportRes.ok) {
        const data = await reportRes.json();
        setRiskReport(data);
      }
    } catch (error) {
      console.error('Error fetching risk data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateBankroll = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/risk/set-bankroll?amount=${bankrollInput}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        fetchData();
        setShowSettings(false);
      }
    } catch (error) {
      console.error('Error updating bankroll:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'EXCELLENT': return 'bg-green-500';
      case 'HEALTHY': return 'bg-blue-500';
      case 'WARNING': return 'bg-yellow-500';
      case 'STOP': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getRiskColor = (score) => {
    if (score < 30) return 'text-green-500';
    if (score < 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="risk-section">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-white">Panel de Riesgo</h3>
          <p className="text-xs text-zinc-500">Kelly Criterion automático</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowSettings(!showSettings)} className="h-8">
            <Settings className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchData} className="h-8">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Settings */}
      {showSettings && (
        <Card className="border-blue-500/30 bg-blue-500/10">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-xs text-zinc-400 mb-1 block">Bankroll (€)</label>
                <Input
                  type="number"
                  value={bankrollInput}
                  onChange={(e) => setBankrollInput(e.target.value)}
                  className="h-9 bg-zinc-800 border-zinc-700"
                />
              </div>
              <Button onClick={updateBankroll} size="sm" className="mt-5">Guardar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Banner */}
      {riskReport && (
        <div className={`${getStatusColor(riskReport.status)} rounded-lg p-3 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {riskReport.status === 'STOP' ? <AlertTriangle className="w-5 h-5" /> : 
               riskReport.status === 'EXCELLENT' ? <CheckCircle className="w-5 h-5" /> : 
               <Shield className="w-5 h-5" />}
              <div>
                <p className="font-bold text-sm">{riskReport.status}</p>
                <p className="text-xs opacity-90">{riskReport.recommendation}</p>
              </div>
            </div>
            <Badge variant="outline" className="bg-white/20 text-white border-white/30 text-xs">
              {riskReport.can_bet ? 'Activo' : 'Pausado'}
            </Badge>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-zinc-500" />
              <span className="text-[10px] text-zinc-500">Bankroll</span>
            </div>
            <p className="text-xl font-bold text-white">€{dashboard?.bankroll?.current?.toLocaleString() || '1,000'}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-zinc-500" />
              <span className="text-[10px] text-zinc-500">Exposición</span>
            </div>
            <p className="text-xl font-bold text-white">{dashboard?.exposure?.total_percent?.toFixed(1) || '0'}%</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-zinc-500" />
              <span className="text-[10px] text-zinc-500">P&L Hoy</span>
            </div>
            <p className={`text-xl font-bold ${(riskReport?.daily_pnl?.amount || 0) >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
              {(riskReport?.daily_pnl?.amount || 0) >= 0 ? '+' : ''}€{riskReport?.daily_pnl?.amount?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-3 pb-3">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-zinc-500" />
              <span className="text-[10px] text-zinc-500">Riesgo</span>
            </div>
            <p className={`text-xl font-bold ${getRiskColor(dashboard?.risk_score || 0)}`}>
              {dashboard?.risk_score || 0}/100
            </p>
            <Progress value={dashboard?.risk_score || 0} className="h-1 mt-1" />
          </CardContent>
        </Card>
      </div>

      {/* P&L Semanal y Límites */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-sm text-white">P&L Semanal</CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <p className={`text-2xl font-bold ${(riskReport?.weekly_pnl?.amount || 0) >= 0 ? 'text-[#00E599]' : 'text-[#F43F5E]'}`}>
              {(riskReport?.weekly_pnl?.amount || 0) >= 0 ? '+' : ''}€{riskReport?.weekly_pnl?.amount?.toFixed(2) || '0.00'}
            </p>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between text-zinc-400">
                <span>Pérdida diaria restante:</span>
                <span className="text-white">€{riskReport?.limits?.daily_loss_remaining?.toFixed(2) || '100.00'}</span>
              </div>
              <div className="flex justify-between text-zinc-400">
                <span>Pérdida semanal restante:</span>
                <span className="text-white">€{riskReport?.limits?.weekly_loss_remaining?.toFixed(2) || '200.00'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-sm text-white">Exposición por Mercado</CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="space-y-2">
              {dashboard?.exposure?.by_market && Object.entries(dashboard.exposure.by_market).slice(0, 4).map(([market, data]) => (
                <div key={market} className="flex items-center justify-between text-xs">
                  <span className="text-zinc-400">{market}</span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="border-zinc-700 text-zinc-400 text-[10px] h-5">{data.count}</Badge>
                    <span className="text-white">€{data.amount.toFixed(0)}</span>
                  </div>
                </div>
              ))}
              {(!dashboard?.exposure?.by_market || Object.keys(dashboard.exposure.by_market).length === 0) && (
                <p className="text-zinc-500 text-xs">Sin exposición activa</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Picks con Kelly */}
      {dashboard?.today_picks?.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2 pt-3">
            <CardTitle className="text-sm text-white flex items-center gap-2">
              <Target className="w-4 h-4" />
              Picks Hoy (Kelly)
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="space-y-2">
              {dashboard.today_picks.slice(0, 5).map((pick, idx) => (
                <div key={pick.id || idx} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white truncate">{pick.match}</p>
                    <p className="text-[10px] text-zinc-500 truncate">{pick.prediction}</p>
                  </div>
                  <div className="text-right ml-2">
                    <p className="text-sm font-bold text-blue-400">€{pick.kelly_stake?.toFixed(0) || '0'}</p>
                    <Badge className={`text-[9px] ${
                      pick.risk_level === 'conservative' ? 'bg-green-500/20 text-green-400' :
                      pick.risk_level === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {pick.risk_level}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sistema Info */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <div className="bg-zinc-800/30 rounded-lg p-2">
          <CheckCircle className="w-4 h-4 text-green-500 mx-auto mb-1" />
          <p className="text-[10px] text-zinc-400">Kelly 25%</p>
        </div>
        <div className="bg-zinc-800/30 rounded-lg p-2">
          <Shield className="w-4 h-4 text-blue-500 mx-auto mb-1" />
          <p className="text-[10px] text-zinc-400">Max 5%</p>
        </div>
        <div className="bg-zinc-800/30 rounded-lg p-2">
          <AlertTriangle className="w-4 h-4 text-yellow-500 mx-auto mb-1" />
          <p className="text-[10px] text-zinc-400">Stop 10%</p>
        </div>
        <div className="bg-zinc-800/30 rounded-lg p-2">
          <Activity className="w-4 h-4 text-purple-500 mx-auto mb-1" />
          <p className="text-[10px] text-zinc-400">Auto</p>
        </div>
      </div>
    </div>
  );
}
